import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { decodeToken, isTokenExpired } from './utils/jwt';
import { trackEvent } from './utils/amplitude';
import api from './api';
import OnboardingFlow from './OnboardingFlow';
import { useDarkMode } from './DarkModeContext';
import { useWorkspace } from './WorkspaceContext';
import './WorkspaceManagement.css';

const OnboardingPage = () => {
    const { darkMode } = useDarkMode();
    const { currentWorkspace, updateWorkspace, switchWorkspace } = useWorkspace();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [userName, setUserName] = useState('');
    const [userId, setUserId] = useState(null);
    
    // Onboarding state - start at step 0 (referral source re-introduced)
    const [onboardingStep, setOnboardingStep] = useState(0);
    
    // Onboarding form data
    const [onboardingProfile, setOnboardingProfile] = useState({
        companyPurpose: '',
        targetAudience: '',
        targetChallenge: '',
        competitors: ''
    });
    
    // Voice cloning for onboarding
    const [onboardingRecordings, setOnboardingRecordings] = useState([]);
    const [isRecordingOnboarding, setIsRecordingOnboarding] = useState(false);
    const [recordingDurationOnboarding, setRecordingDurationOnboarding] = useState(0);
    const [recordingIntervalOnboarding, setRecordingIntervalOnboarding] = useState(null);
    const [isSavingRecordingOnboarding, setIsSavingRecordingOnboarding] = useState(false);
    const [onboardingVoiceId, setOnboardingVoiceId] = useState('');
    const [isGeneratingVoiceOnboarding, setIsGeneratingVoiceOnboarding] = useState(false);
    
    // Video upload for onboarding
    const [onboardingVideos, setOnboardingVideos] = useState([]);
    const [onboardingCuts, setOnboardingCuts] = useState([]);
    const [onboardingUploadProgress, setOnboardingUploadProgress] = useState({});
    
    // Script and video generation for onboarding
    const [onboardingScript, setOnboardingScript] = useState(null);
    const [onboardingVoiceoverUrl, setOnboardingVoiceoverUrl] = useState('');
    const [onboardingVideoUrl, setOnboardingVideoUrl] = useState('');
    const [voiceoverDuration, setVoiceoverDuration] = useState(0);
    const [isGeneratingScriptOnboarding, setIsGeneratingScriptOnboarding] = useState(false);
    const [isGeneratingVoiceoverOnboarding, setIsGeneratingVoiceoverOnboarding] = useState(false);
    const [isGeneratingVideoOnboarding, setIsGeneratingVideoOnboarding] = useState(false);
    const [isDownloadingVideoOnboarding, setIsDownloadingVideoOnboarding] = useState(false);
    const [isSavingVideoOnboarding, setIsSavingVideoOnboarding] = useState(false);
    
    // AI suggestion loading state
    const [aiSuggestionLoading, setAiSuggestionLoading] = useState({
        targetAudience: false,
        targetChallenge: false,
        competitors: false
    });

    // Completion glow and sound (script / voiceover / video / download)
    const [completionGlowActive, setCompletionGlowActive] = useState(false);
    const completionGlowTimeoutRef = useRef(null);
    const COMPLETION_GLOW_MS = 1450;
    const triggerCompletionGlow = (colors) => {
        try {
            const palette = colors?.length === 2 ? colors : ['#22c55e', '#16a34a'];
            if (completionGlowTimeoutRef.current) clearTimeout(completionGlowTimeoutRef.current);
            setCompletionGlowActive(false);
            document.body.classList.remove('wm-completion-glow');
            requestAnimationFrame(() => {
                setCompletionGlowActive(true);
                try {
                    document.body.style.setProperty('--wm-glow-1', palette[0]);
                    document.body.style.setProperty('--wm-glow-2', palette[1]);
                } catch (e) {}
                document.body.classList.add('wm-completion-glow');
                completionGlowTimeoutRef.current = setTimeout(() => {
                    setCompletionGlowActive(false);
                    document.body.classList.remove('wm-completion-glow');
                }, COMPLETION_GLOW_MS);
            });
        } catch (e) {}
    };
    const playCompletionSound = (soundName) => {
        try {
            const path = soundName.includes('.') ? `/audio/${soundName}` : `/audio/${soundName}.mp3`;
            const audio = new Audio(path);
            audio.volume = 0.3;
            audio.play().catch(err => console.warn('Failed to play completion sound:', err));
        } catch (error) {
            console.warn('Error playing completion sound:', error);
        }
    };
    useEffect(() => {
        return () => {
            if (completionGlowTimeoutRef.current) clearTimeout(completionGlowTimeoutRef.current);
            try {
                document.body.classList.remove('wm-completion-glow');
                document.body.style.removeProperty('--wm-glow-1');
                document.body.style.removeProperty('--wm-glow-2');
            } catch (e) {}
        };
    }, []);

    useEffect(() => {
        const initializeUser = async () => {
            const token = localStorage.getItem('token');
            
            if (!token || isTokenExpired(token)) {
                navigate('/landing-page');
                return;
            }

            const decoded = decodeToken(token);
            if (decoded) {
                setUserId(decoded.userId);
                setUserName(decoded.name);
                
                try {
                    const profileResponse = await api.getUserProfile();
                    if (profileResponse.data) {
                        // If user has already completed onboarding, redirect to workspace
                        if (profileResponse.data.onboardingCompleted) {
                            navigate('/workspace');
                            return;
                        }
                        
                        // Initialize onboarding profile with workspace data (if available)
                        if (currentWorkspace) {
                            setOnboardingProfile(prev => ({
                                ...prev,
                                companyPurpose: currentWorkspace.prospectingProfile?.companyPurpose || '',
                                targetAudience: currentWorkspace.prospectingProfile?.targetAudience || '',
                                targetChallenge: currentWorkspace.prospectingProfile?.targetChallenge || '',
                                competitors: currentWorkspace.prospectingProfile?.competitors || ''
                            }));
                            
                            // Always refresh workspace data to get latest recordings and voiceId
                            try {
                                const workspaceResponse = await api.getWorkspace(currentWorkspace._id);
                                const workspace = workspaceResponse.data;
                                
                                // Load existing workspace recordings (always set, even if empty)
                                if (workspace.recordings) {
                                    setOnboardingRecordings(workspace.recordings);
                                } else {
                                    setOnboardingRecordings([]);
                                }
                                
                                // Load existing workspace voice ID
                                if (workspace.voiceId) {
                                    setOnboardingVoiceId(workspace.voiceId);
                                } else {
                                    // Clear voiceId if no recordings exist
                                    if (!workspace.recordings || workspace.recordings.length === 0) {
                                        setOnboardingVoiceId('');
                                    }
                                }
                            } catch (error) {
                                console.error('Error fetching workspace data:', error);
                                // Fallback to using currentWorkspace data if API call fails
                                if (currentWorkspace.recordings && currentWorkspace.recordings.length > 0) {
                                    setOnboardingRecordings(currentWorkspace.recordings);
                                } else {
                                    setOnboardingRecordings([]);
                                }
                                if (currentWorkspace.voiceId) {
                                    setOnboardingVoiceId(currentWorkspace.voiceId);
                                }
                            }
                        }
                        
                        trackEvent('onboarding_started', { userId: decoded.userId });
                    }
                } catch (error) {
                    console.error('Error initializing onboarding:', error);
                    navigate('/landing-page');
                    return;
                }
            }
            
            setIsLoading(false);
        };

        initializeUser();
    }, [navigate, currentWorkspace]);

    const handleOnboardingNext = () => {
        trackEvent('onboarding_step_completed', { 
            step: onboardingStep,
            userId: userId 
        });
        setOnboardingStep(prev => {
            // Skip steps 4, 5, 6 and go directly to step 6 from step 3
            if (prev === 3) {
                return 6;
            }
            return prev + 1;
        });
    };

    const handleOnboardingPrevious = () => {
        trackEvent('onboarding_step_back', { 
            step: onboardingStep,
            userId: userId 
        });
        setOnboardingStep(prev => {
            // Skip steps 4, 5, 6 when going back from step 6
            if (prev === 6) {
                return 3;
            }
            return Math.max(0, prev - 1);
        });
    };

    const handleOnboardingSkip = async () => {
        trackEvent('onboarding_skipped', { 
            step: onboardingStep,
            userId: userId 
        });
        
        try {
            await api.completeOnboarding();
            navigate('/workspace');
        } catch (error) {
            console.error('Error completing onboarding:', error);
        }
    };

    const handleOnboardingComplete = async () => {
        trackEvent('onboarding_completed', { 
            userId: userId,
            hasVoice: !!onboardingVoiceId,
            hasVideos: onboardingVideos.length > 0,
            hasGeneratedVideo: !!onboardingVideoUrl
        });
        
        try {
            await api.completeOnboarding();
            
            // Refresh workspace to get updated voiceId if voice was cloned during onboarding
            if (currentWorkspace?._id) {
                try {
                    await switchWorkspace(currentWorkspace._id);
                } catch (refreshError) {
                    console.error('Error refreshing workspace after onboarding:', refreshError);
                    // Continue navigation even if refresh fails
                }
            }
            
            navigate('/workspace');
        } catch (error) {
            console.error('Error completing onboarding:', error);
        }
    };

    const handleAISuggestion = async (field) => {
        if (!onboardingProfile.companyPurpose) {
            return;
        }

        // Set loading state for this field
        setAiSuggestionLoading(prev => ({
            ...prev,
            [field]: true
        }));

        try {
            const response = await api.getAISuggestion(field, onboardingProfile.companyPurpose);
            setOnboardingProfile(prev => ({
                ...prev,
                [field]: response.suggestion
            }));
            trackEvent('ai_suggestion_used', { 
                field,
                userId: userId 
            });
        } catch (error) {
            console.error('Error getting AI suggestion:', error);
        } finally {
            // Clear loading state for this field
            setAiSuggestionLoading(prev => ({
                ...prev,
                [field]: false
            }));
        }
    };

    const handleOnboardingProfileSave = async () => {
        try {
            if (currentWorkspace) {
                // Update workspace profile
                await updateWorkspace(currentWorkspace._id, {
                    prospectingProfile: {
                        ...currentWorkspace.prospectingProfile,
                        ...onboardingProfile
                    }
                });
            } else {
                // Fallback to user profile if no workspace
                await api.updateProfile({
                    prospectingProfile: {
                        ...onboardingProfile
                    }
                });
            }
            
            trackEvent('onboarding_profile_saved', { 
                userId: userId,
                hasCompanyPurpose: !!onboardingProfile.companyPurpose,
                hasTargetAudience: !!onboardingProfile.targetAudience,
                hasTargetChallenge: !!onboardingProfile.targetChallenge,
                hasCompetitors: !!onboardingProfile.competitors
            });
            
            // Don't automatically advance to next step - let the button handle navigation
        } catch (error) {
            console.error('Error saving profile:', error);
        }
    };

    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: darkMode ? '#27292d' : '#fff'
            }}>
                <div style={{
                    fontSize: '18px',
                    color: darkMode ? '#fff' : '#333'
                }}>
                    Loading onboarding...
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: darkMode ? '#1a1a1a' : '#fff',
            padding: '20px'
        }}>
                <OnboardingFlow 
                    step={onboardingStep}
                    userName={userName}
                    onboardingProfile={onboardingProfile}
                    setOnboardingProfile={setOnboardingProfile}
                    onboardingRecordings={onboardingRecordings}
                    setOnboardingRecordings={setOnboardingRecordings}
                    isRecordingOnboarding={isRecordingOnboarding}
                    setIsRecordingOnboarding={setIsRecordingOnboarding}
                    recordingDurationOnboarding={recordingDurationOnboarding}
                    setRecordingDurationOnboarding={setRecordingDurationOnboarding}
                    recordingIntervalOnboarding={recordingIntervalOnboarding}
                    setRecordingIntervalOnboarding={setRecordingIntervalOnboarding}
                    isSavingRecordingOnboarding={isSavingRecordingOnboarding}
                    setIsSavingRecordingOnboarding={setIsSavingRecordingOnboarding}
                    onboardingVoiceId={onboardingVoiceId}
                    setOnboardingVoiceId={setOnboardingVoiceId}
                    isGeneratingVoiceOnboarding={isGeneratingVoiceOnboarding}
                    setIsGeneratingVoiceOnboarding={setIsGeneratingVoiceOnboarding}
                    onboardingVideos={onboardingVideos}
                    setOnboardingVideos={setOnboardingVideos}
                    onboardingCuts={onboardingCuts}
                    setOnboardingCuts={setOnboardingCuts}
                    onboardingUploadProgress={onboardingUploadProgress}
                    setOnboardingUploadProgress={setOnboardingUploadProgress}
                    onboardingScript={onboardingScript}
                    setOnboardingScript={setOnboardingScript}
                    onboardingVoiceoverUrl={onboardingVoiceoverUrl}
                    setOnboardingVoiceoverUrl={setOnboardingVoiceoverUrl}
                    voiceoverDuration={voiceoverDuration}
                    setVoiceoverDuration={setVoiceoverDuration}
                    onboardingVideoUrl={onboardingVideoUrl}
                    setOnboardingVideoUrl={setOnboardingVideoUrl}
                    isGeneratingScriptOnboarding={isGeneratingScriptOnboarding}
                    setIsGeneratingScriptOnboarding={setIsGeneratingScriptOnboarding}
                    isGeneratingVoiceoverOnboarding={isGeneratingVoiceoverOnboarding}
                    setIsGeneratingVoiceoverOnboarding={setIsGeneratingVoiceoverOnboarding}
                    isGeneratingVideoOnboarding={isGeneratingVideoOnboarding}
                    setIsGeneratingVideoOnboarding={setIsGeneratingVideoOnboarding}
                    isDownloadingVideoOnboarding={isDownloadingVideoOnboarding}
                    setIsDownloadingVideoOnboarding={setIsDownloadingVideoOnboarding}
                    isSavingVideoOnboarding={isSavingVideoOnboarding}
                    setIsSavingVideoOnboarding={setIsSavingVideoOnboarding}
                    onNext={handleOnboardingNext}
                    onPrevious={handleOnboardingPrevious}
                    onSkip={handleOnboardingSkip}
                    onComplete={handleOnboardingComplete}
                    onAISuggestion={handleAISuggestion}
                    onProfileSave={handleOnboardingProfileSave}
                    currentWorkspace={currentWorkspace}
                    aiSuggestionLoading={aiSuggestionLoading}
                    triggerCompletionGlow={triggerCompletionGlow}
                    playCompletionSound={playCompletionSound}
                />
        </div>
    );
};

export default OnboardingPage;
